// Translation API service
import { withAuth } from "./auth0-service";

const API_URL = import.meta.env.VITE_API_BASE_URL || "https://localhost:7122/api";

class TranslationApi {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.cacheTTL = 30 * 60 * 1000; // 30 minutes
  }

  /**
   * Load translations for a specific language
   * @param {string} language - Language code (e.g., 'en', 'mk')
   * @param {boolean} force - Force refresh from server, bypass cache
   * @returns {Promise<Object>} Translation object
   */
  async loadTranslations(language = "en", force = false) {
    const cacheKey = `translations_${language}`;

    // Check cache first (unless force refresh)
    if (!force && this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    try {
      const options = await withAuth();
      const response = await fetch(
        `${API_URL}/translations/${language}`,
        options
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(
          error ||
            `Failed to load translations: ${response.status} ${response.statusText}`
        );
      }

      const translations = await response.json();

      // Cache the result
      this.cache.set(cacheKey, translations);
      this.cacheExpiry.set(cacheKey, Date.now() + this.cacheTTL);

      return translations;
    } catch (error) {
      console.error("Error loading translations:", error);

      // Try to return cached version as fallback
      if (this.cache.has(cacheKey)) {
        console.warn("Using cached translations as fallback");
        return this.cache.get(cacheKey);
      }

      // If no cache and not English, try to fallback to English
      if (language !== "en") {
        console.warn("Falling back to English translations");
        return this.loadTranslations("en", force);
      }

      // Return empty object if all else fails
      console.error("No translations available, returning empty object");
      return {};
    }
  }

  /**
   * Get available languages from the server
   * @returns {Promise<string[]>} Array of language codes
   */
  async getAvailableLanguages() {
    const cacheKey = "available_languages";

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    try {
      const options = await withAuth();
      const response = await fetch(
        `${API_URL}/translations/languages`,
        options
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(
          error || `Failed to load available languages: ${response.status}`
        );
      }

      const languages = await response.json();

      // Cache the result
      this.cache.set(cacheKey, languages);
      this.cacheExpiry.set(cacheKey, Date.now() + this.cacheTTL);

      return languages;
    } catch (error) {
      console.error("Error loading available languages:", error);

      // Return default languages as fallback
      return ["en", "mk"];
    }
  }

  /**
   * Check if cached data is still valid
   * @param {string} cacheKey - Cache key to check
   * @returns {boolean} True if cache is valid
   */
  isCacheValid(cacheKey) {
    if (!this.cache.has(cacheKey) || !this.cacheExpiry.has(cacheKey)) {
      return false;
    }

    return Date.now() < this.cacheExpiry.get(cacheKey);
  }

  /**
   * Clear translation cache
   * @param {string} language - Optional language to clear, if not provided clears all
   */
  clearCache(language = null) {
    if (language) {
      const cacheKey = `translations_${language}`;
      this.cache.delete(cacheKey);
      this.cacheExpiry.delete(cacheKey);
    } else {
      this.cache.clear();
      this.cacheExpiry.clear();
    }
  }

  /**
   * Preload translations for multiple languages
   * @param {string[]} languages - Array of language codes to preload
   * @returns {Promise<Object>} Object with language codes as keys and translations as values
   */
  async preloadTranslations(languages = ["en", "mk"]) {
    const promises = languages.map((lang) =>
      this.loadTranslations(lang).then((translations) => ({
        [lang]: translations,
      }))
    );

    try {
      const results = await Promise.all(promises);
      return results.reduce((acc, result) => ({ ...acc, ...result }), {});
    } catch (error) {
      console.error("Error preloading translations:", error);
      return {};
    }
  }

  /**
   * Get user's default language preference from server
   * @returns {Promise<string>} User's preferred language code
   */
  async getUserLanguagePreference() {
    try {
      const options = await withAuth();
      const response = await fetch(`${API_URL}/Project/language`, options);

      if (!response.ok) {
        const error = await response.text();
        throw new Error(
          error ||
            `Failed to get language preference: ${response.status} ${response.statusText}`
        );
      }

      const project = await response.json();
      return project.preferredLanguage || project.language || "en"; // Return preferredLanguage or fallback to language or default to 'en'
    } catch (error) {
      console.error("Error getting user language preference:", error);
      // Return default language as fallback
      return "en";
    }
  }

  /**
   * Set user's default language preference on server
   * @param {string} language - Language code to set as default
   * @returns {Promise<Object>} Updated project data
   */
  async setUserLanguagePreference(language) {
    try {
      const options = await withAuth();
      const response = await fetch(`${API_URL}/Project/language`, {
        ...options,
        method: "PUT",
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          preferredLanguage: language,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(
          error ||
            `Failed to set language preference: ${response.status} ${response.statusText}`
        );
      }

      const project = await response.json();
      return project;
    } catch (error) {
      console.error("Error setting user language preference:", error);
      throw error; // Re-throw to allow caller to handle
    }
  }
}

// Export singleton instance
export const translationApi = new TranslationApi();
export default translationApi;
